// External libraries
import Grid from '@mui/material/Grid2';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FormControl, RadioGroup, FormControlLabel, Radio, MenuItem, FormLabel, FormHelperText } from '@mui/material';

// API
import { useCreateFreeFieldMutation, useUpdateFreeFieldMutation } from '@api/free-fields/free-fields-api';

// Models
import { FreeFieldBaseApiModel } from '@models/ApiModels/free-field-base-api-model';
import { FreeFieldBase } from '@models/DomainModels/Master/free-field-base';

// Common components
import { CdfFormField } from '@commonComponents/forms/fields/CdfFormField';
import { EntityDetailDialog, EntityDetailDialogWrapper } from '@commonComponents/dialogs/EntityDetailDialog';
import { useEntityDetail } from '@commonComponents/dialogs/EntityDetailDialog/useEntityDetail';

// State management
import { DynamicStore } from '@state/interfaces/idynamic-store';

// Form Validation
import { FormFactory } from '@services/validation/form-factory';
import { useDecoratorForm } from '@hooks/useDecoratorForm';

// Permissions
import { useFetchToken } from '@hooks/useFetchToken';

import { FreeFieldValueType } from '../../../../models/enums/FreeFieldValueType';

import { FreeFieldEntityType } from '@models/enums/FreeFieldEntityType';
import type { EntityDialogCloseResult } from '@commonComponents/dialogs/EntityDetailDialog/useEntityDetail';

type FreeFieldDetailDialogWrapperProps = {
  entityData?: FreeFieldBase;
  organizationId: string;
  open: boolean;
  onClose: (result: EntityDialogCloseResult<FreeFieldBase>) => void;
  store: DynamicStore | undefined;
};

type FreeFieldDetailDialogInternalProps = FreeFieldDetailDialogWrapperProps & {
  token: string;
  readPermission?: boolean;
  writePermission?: boolean;
};

/**
 * Dialog component for creating and editing free fields
 * Uses EntityDetailDialog as base component and follows CDF patterns
 */
function FreeFieldDetailDialog({
  entityData,
  organizationId,
  open,
  onClose,
  token,
  readPermission = false,
  writePermission = false
}: Readonly<FreeFieldDetailDialogInternalProps>) {
  const { t } = useTranslation(['freeFields', 'common', 'enums']);

  // RTK Query hooks
  const [createFreeField] = useCreateFreeFieldMutation();
  const [updateFreeField] = useUpdateFreeFieldMutation();

  // Initialize form using FormFactory
  const formConfig = FormFactory.getFormConfig(
    FreeFieldBaseApiModel,
    entityData ?? {
      name: '',
      valueType: '',
      entityType: FreeFieldEntityType.BusinessPartner
    },
    { showSnackbarOnError: true }
  );

  const {
    data: freeFieldData,
    errors: validationErrors,
    isValid: formIsValid,
    updateField,
    validateField,
    validateForm,
    resetForm,
    setData,
    setFormChanged,
    isDirty
  } = useDecoratorForm(formConfig.modelClass, formConfig.initialData, formConfig.options);

  // Memoize the isDirty value to prevent excessive re-renders
  const isDirtyValue = useMemo(() => isDirty(), [isDirty]);

  // Check for required fields having values
  // This ensures the save button is disabled until all required fields have values
  const hasRequiredFieldValues = useMemo(() => {
    return !!freeFieldData.name?.trim() && !!freeFieldData.valueType?.trim() && !!freeFieldData.entityType?.trim();
  }, [freeFieldData.name, freeFieldData.valueType, freeFieldData.entityType]);

  // Combine validations - the form is valid only when all required fields are filled AND validation passes
  const isValid = useMemo(() => {
    return formIsValid && hasRequiredFieldValues;
  }, [formIsValid, hasRequiredFieldValues]);

  // Create an enhanced wrapper for validateForm with better logging and error handling
  const validateFormWrapper = useCallback(async (): Promise<boolean> => {
    // Run the decorator-based validation
    return (await validateForm()).isValid;
  }, [validateForm]);

  const createEntity = useCallback(async (): Promise<FreeFieldBase> => {
    const result = await createFreeField({
      organizationId,
      freeFieldBase: {
        name: freeFieldData.name ?? '',
        valueType: freeFieldData.valueType ?? '',
        entityType: freeFieldData.entityType ?? ''
      },
      token
    }).unwrap();

    return {
      id: result,
      organizationId,
      name: freeFieldData.name ?? '',
      valueType: freeFieldData.valueType ?? '',
      entityType: freeFieldData.entityType ?? ''
    };
  }, [createFreeField, organizationId, token, freeFieldData]);

  const updateEntity = useCallback(async (): Promise<FreeFieldBase> => {
    await updateFreeField({
      organizationId,
      freeFieldId: entityData?.id ?? '',
      freeFieldBase: {
        name: freeFieldData.name ?? '',
        valueType: freeFieldData.valueType ?? '',
        entityType: freeFieldData.entityType ?? ''
      },
      token
    }).unwrap();

    return {
      id: entityData?.id ?? '',
      organizationId,
      name: freeFieldData.name ?? '',
      valueType: freeFieldData.valueType ?? '',
      entityType: freeFieldData.entityType ?? ''
    };
  }, [updateFreeField, organizationId, entityData?.id, token, freeFieldData]);

  const handleDialogClose = useCallback(
    (result: EntityDialogCloseResult<FreeFieldBase>) => {
      resetForm();
      setFormChanged(false);
      onClose(result);
    },
    [onClose, resetForm, setFormChanged]
  );

  const { handleSave, loadingState } = useEntityDetail<FreeFieldBase>({
    entityName: t('entityName'),
    initialData: {
      ...freeFieldData,
      id: entityData?.id ?? '',
      organizationId,
      name: freeFieldData.name ?? '',
      valueType: freeFieldData.valueType ?? '',
      entityType: freeFieldData.entityType ?? ''
    },
    open,
    onClose: handleDialogClose,
    createEntity,
    updateEntity,
    validateForm: validateFormWrapper,
    setData,
    resetForm,
    loggerContext: 'FreeFieldDetailDialog',
    readPermission,
    writePermission,
    setFormChanged
  });

  // Handle field changes
  const handleFieldChange = useCallback(
    (field: string, value: string | number) => {
      updateField(field, value);

      // Ensure we validate the field after updating
      setTimeout(() => validateField(field, value), 0);
    },
    [updateField, validateField]
  );

  // Handle field blur (validation)
  const handleFieldBlur = useCallback(
    (field: string) => {
      validateField(field, freeFieldData[field as keyof FreeFieldBaseApiModel]);
    },
    [validateField, freeFieldData]
  );

  // Create array of available text types from enum
  const availableValueTypes = useMemo(
    () =>
      Object.entries(FreeFieldValueType).map(([key, value]) => ({
        key,
        value
      })),
    []
  );
  // Create array of available text types from enum
  const availableEntityTypes = useMemo(
    () =>
      Object.entries(FreeFieldEntityType).map(([key, value]) => ({
        key,
        value
      })),
    []
  );

  // Create form content
  const formContent = (
    <Grid container size={12}>
      {/* Name Field */}
      <Grid size={12}>
        <CdfFormField
          name="name"
          label={t('detailDialog.name')}
          value={freeFieldData.name}
          required
          onChange={handleFieldChange}
          onBlur={handleFieldBlur}
          errors={validationErrors}
          data-testid="free-field-name-field"
          slotProps={{
            input: {
              'aria-label': t('detailDialog.name'),
              readOnly: !writePermission
            }
          }}
        />
      </Grid>

      <Grid size={12}>
        <FormControl fullWidth required error={!!validationErrors['entityType']} data-testid="entityType-field">
          <FormLabel>{t('detailDialog.entityType')}</FormLabel>
          <RadioGroup
            row
            name="entityType"
            value={freeFieldData.entityType}
            onChange={e => handleFieldChange('entityType', e.target.value)}
            onBlur={() => handleFieldBlur('entityType')}
            aria-label={t('detailDialog.entityType')}>
            {availableEntityTypes.map(option => (
              <FormControlLabel
                key={option.key}
                value={option.value}
                control={<Radio disabled={!writePermission} />}
                label={t(`enums:freeFieldEntityType.${option.key}`)}
              />
            ))}
          </RadioGroup>
          {validationErrors['entityType'] && <FormHelperText>{t(validationErrors['entityType'])}</FormHelperText>}
        </FormControl>
      </Grid>

      <Grid size={12}>
        <CdfFormField
          name="valueType"
          label={t('detailDialog.valueType')}
          value={freeFieldData.valueType}
          required
          select
          onChange={handleFieldChange}
          onBlur={handleFieldBlur}
          errors={validationErrors}
          data-testid="value-type-field"
          slotProps={{
            input: {
              'aria-label': t('detailDialog.valueType'),
              readOnly: !writePermission
            }
          }}
          fullWidth>
          {availableValueTypes.map(option => (
            <MenuItem key={option.key} value={option.value}>
              {t(`enums:freeFieldValueType.${option.key}`)}
            </MenuItem>
          ))}
        </CdfFormField>
      </Grid>
    </Grid>
  );

  // Get save button text based on edit vs. create mode
  const saveButtonText = entityData?.id ? t('saveChanges') : t('addButtonText');

  // Get cancel button text based on write permission
  const cancelButtonText = writePermission ? t('common:cancel') : t('common:close');

  // Get edit mode display title based on permissions
  const editModeTitle = writePermission ? t('edit') : t('view');

  // Get title text based on whether we're editing or creating
  const titleText = entityData?.id ? editModeTitle : t('add');

  return (
    <EntityDetailDialog
      open={open}
      onClose={() => handleDialogClose({ action: 'cancelled' })}
      title={titleText}
      formContent={formContent}
      saveButtonText={saveButtonText}
      cancelButtonText={cancelButtonText}
      readPermission={readPermission}
      writePermission={writePermission}
      isLoading={false}
      loadingState={loadingState}
      loadingMessage={t('detailDialog.loadingMessage')}
      savingMessage={t('detailDialog.savingMessage')}
      isValid={isValid}
      isDirty={isDirtyValue}
      onSave={handleSave}
      dataTestId="free-field-detail-dialog"
      maxWidth="xs"
    />
  );
}

/**
 * Wrapper component for FreeFieldDetailDialog
 * Provides necessary context, permissions, and error boundaries
 */
export function FreeFieldDetailDialogWrapper({ entityData, open, onClose, organizationId, store }: Readonly<FreeFieldDetailDialogWrapperProps>) {
  const token = useFetchToken();

  return (
    <EntityDetailDialogWrapper
      organizationId={organizationId}
      store={store}
      loggerContext="FreeFieldDetailDialog"
      dataTestId="free-field-detail-error-boundary-wrapper"
      entityType="FreeFields"
      renderContent={permissions => (
        <FreeFieldDetailDialog
          entityData={entityData}
          open={open}
          onClose={onClose}
          organizationId={organizationId}
          store={store}
          token={token}
          readPermission={permissions.readPermission}
          writePermission={permissions.writePermission}
        />
      )}
    />
  );
}
